// Hand-curated lists that DO NOT change with the DB schema.
//
// Use this file for things like ISO currency codes, country codes,
// fixed-format AQL levels, or business-policy buckets that are
// intentionally hand-managed. For anything backed by a DB CHECK
// constraint or pg_enum, use src/lib/db-enums.generated.ts instead.
//
// Curated 2026-05-04 during the W9 dropdowns audit so the static lists
// stop appearing inline in page components.

import { PRODUCTION_SAMPLES_KIND } from "./db-enums.generated";

/**
 * Sample-type options offered in the library add/edit forms.
 *
 * `samples.library_items.sample_type` is FREE TEXT (no CHECK constraint), so
 * this is a UI suggestion list, not a DB-enforced enum. It is the production
 * sample kinds (db-enums.generated) plus library-only extras that never appear
 * in `production.samples.kind`.
 */
export const LIBRARY_SAMPLE_TYPES = [
  ...PRODUCTION_SAMPLES_KIND,
  "bought_sample",
] as const;
export type LibrarySampleType = (typeof LIBRARY_SAMPLE_TYPES)[number];

/** ISO 4217 currencies that CC actually uses on quotes / costings. */
export const QUOTE_CURRENCIES = ["USD", "CNY", "ZAR", "EUR", "GBP"] as const;
export type QuoteCurrency = (typeof QUOTE_CURRENCIES)[number];

/** AQL acceptance quality levels expressed as percentages. */
export const AQL_LEVELS = ["2.5", "1.0", "0.65"] as const;
export type AqlLevel = (typeof AQL_LEVELS)[number];

/** QA inspection types — Mac uses these three buckets, hand-curated. */
export const QA_INSPECTION_TYPES = ["inline", "final", "pre-shipment"] as const;
export type QaInspectionType = (typeof QA_INSPECTION_TYPES)[number];

/**
 * `production.qa_inspections.next_action` — mirrors the CHECK constraint as of
 * migration 6168 (#1071, epic #1069 phase 1b), which added `discount`.
 *
 * Hand-maintained here rather than read from `db-enums.generated.ts` because
 * each entry carries a human-facing label, not just the DB value — the same
 * convention as ORDER_CHANNELS and PACKING_TYPES below. (Until #1110 there was
 * a second reason: the generated file was a 2026-05 snapshot that still listed
 * the four pre-6168 values. It has since been regenerated and does cover this
 * CHECK, so keep the two value lists in step.)
 *
 * On a NON-pass these are design §2.4's fail resolutions:
 *   rework   — repair + re-audit (then "Re-inspect")
 *   discount — ship at an agreed discount
 *   ship     — ship with the supplier liable
 * `ship` and `discount` additionally require the customer's recorded
 * permission to ship (D29, questions-file ruling 20) — the server refuses them
 * without it.
 */
export const QA_NEXT_ACTIONS = [
  { value: "ship", label: "Ship — release for shipment" },
  { value: "hold", label: "Hold — pause" },
  { value: "rework", label: "Rework — supplier fixes, then re-inspect" },
  { value: "reject", label: "Reject — refund / cancel" },
  { value: "discount", label: "Discount — ship at an agreed discount" },
] as const;
export type QaNextAction = (typeof QA_NEXT_ACTIONS)[number]["value"];

/** Customer tier — strategic / important / minor. */
export const CUSTOMER_TIERS = [
  { value: 1, label: "Tier 1 — strategic" },
  { value: 2, label: "Tier 2 — important" },
  { value: 3, label: "Tier 3 — minor" },
] as const;

/**
 * `production.orders.channel` — mirrors the CHECK constraint added by
 * migration 6077 (ruling D3): customer = a normal customer order; corporate =
 * CC buying its own Atomic-label range into stock; cc_stock = any other stock
 * CC holds to sell (channel rows carry no customer). Not a pg_enum, so this
 * list is hand-maintained rather than generated — see db-enums.generated.ts's
 * header for the pg_enum-only scope of that generator.
 */
export const ORDER_CHANNELS = [
  { value: "customer", label: "Customer" },
  { value: "cc_corporate_range", label: "CC corporate range" },
  { value: "cc_stock", label: "CC stock" },
] as const;
export type OrderChannel = (typeof ORDER_CHANNELS)[number]["value"];

/**
 * `production.orders.packing_type` — mirrors the CHECK constraint added by
 * migration 6154 (design D23): which packing sub-model this order's "packs"
 * lines use — solid (single-size cartons, order_lines.pieces_per_carton) or
 * ratio (mixed-size cartons via size_prepacks, unchanged from 6057). Not a
 * pg_enum, so hand-maintained like ORDER_CHANNELS above. No "not chosen"
 * entry here — that is the unselected/placeholder state, not a value.
 */
export const PACKING_TYPES = [
  { value: "solid", label: "Solid (single size per carton)" },
  { value: "ratio", label: "Ratio (mixed sizes per carton)" },
] as const;
export type PackingType = (typeof PACKING_TYPES)[number]["value"];
